import { useMemo } from 'react';
import { SortDirectionEnum, useFindByQuery } from '../../graphql';
import {
  groupSideChatSummaries,
  SideChatSummary,
  SideChatSummarySource,
} from '../../issues/hooks/useSideChatSummaries';

/** One side-chat pill's worth of data, keyed by the message it hangs off. */
export type AssetChatSideChat = SideChatSummary & { parentMessageId: string };

/**
 * Deliberately smaller than `useSideChatSummaries`' 1000. That hook loads one
 * activity's whole message list; this one loads a *page of activities* at
 * once, so the same cap would be a 50x heavier request. Pills are an index,
 * not a ledger: a feed page whose side chats exceed this is already past the
 * point where more pills would help.
 */
const ASSET_CHAT_SIDE_CHAT_PAGE_SIZE = 1000;

/**
 * Side-chat pills for a whole page of chat feed rows, in **one** request.
 *
 * A feed row is an Issue, and the pills are thread-wide (every side chat
 * anywhere in that Issue's thread, not just the one on its origin message —
 * see plan 0035 Phase 2, Side Chat rules). Calling `useSideChatSummaries` per
 * row would therefore issue one 1000-row `findIssueMessages` per row, 25-50 of
 * them on a real asset. `IssueMessageFilter.issue` takes `in`, so the whole
 * page is one query instead, grouped by issue id here.
 *
 * Not viewer-filtered client-side: `findIssueMessages` applies the access
 * rules server-side, so a side chat the viewer is not in never arrives. That
 * is the same thing the shipped Side Chat surface relies on — filtering here
 * as well would only hide rows the server already withheld.
 */
export const useAssetChatSideChats = (
  issueIds: string[],
  options?: { enabled?: boolean }
) => {
  // Deduplicated and sorted so two renders of the same page produce the same
  // cache key, rather than refetching whenever the feed hands over a new array
  // with the same contents. The joined string is the memo's dependency for the
  // same reason — an array literal would never compare equal.
  const idsKey = [...new Set(issueIds)].sort().join(',');
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);

  const { data, isLoading, refetch, invalidateQuery } = useFindByQuery({
    modelName: 'issueMessage',
    query: FIND_ASSET_CHAT_SIDE_CHATS,
    variables: {
      filter: { issue: { in: ids } },
      page: { limit: ASSET_CHAT_SIDE_CHAT_PAGE_SIZE, offset: 0 },
      orderBy: { field: 'updatedAt', order: SortDirectionEnum.Asc },
    },
    options: { enabled: (options?.enabled ?? true) && ids.length > 0 },
  });

  /**
   * `Map<issueId, AssetChatSideChat[]>`. Grouping runs per issue through the
   * same `groupSideChatSummaries` the single-activity surface uses, so the two
   * agree on what counts as a reply (parented `USER_MESSAGE` only — never the
   * divider) without restating that rule here.
   */
  const sideChatsByIssue = useMemo(() => {
    const items = (data?.items ?? []) as (SideChatSummarySource & {
      issue?: { id?: string } | null;
    })[];

    const byIssue = new Map<string, SideChatSummarySource[]>();
    items.forEach((item) => {
      const issueId = item.issue?.id;
      if (!issueId) return;
      const bucket = byIssue.get(issueId);
      if (bucket) bucket.push(item);
      else byIssue.set(issueId, [item]);
    });

    const result = new Map<string, AssetChatSideChat[]>();
    byIssue.forEach((messages, issueId) => {
      const grouped = groupSideChatSummaries(messages);
      const pills: AssetChatSideChat[] = [];
      grouped.forEach((summary, parentMessageId) =>
        pills.push({ ...summary, parentMessageId })
      );
      if (pills.length) result.set(issueId, pills);
    });
    return result;
  }, [data]);

  return { sideChatsByIssue, isLoading, refetch, invalidateQuery };
};

const FIND_ASSET_CHAT_SIDE_CHATS = `
  query findIssueMessages($filter: IssueMessageFilter, $page: PageRequest, $orderBy: OrderByInput) {
    findIssueMessages(filter: $filter, page: $page, orderBy: $orderBy) {
      items {
        id
        type
        issue {
          id
        }
        parentMessage {
          id
        }
        createdBy {
          name {
            first
            last
          }
        }
      }
      count
      offset
      limit
    }
  }
`;
